/**
 * @author NikolaV
 */
import { Ajax, Ajax2 } from '@shared/Ajax';
import { Notify } from '@shared/ui-native/Notify';

//@ts-ignore
const removeDiacritics = require('diacritics').remove;

function fetchUsersForCurrentProject(): JQueryPromise<IUser[]>  {
    return Ajax({
        url: 'ajax_get_project_users.json',
        method: 'get'
    }).then(resp => {
        resp.users.forEach((u: IUser, index) => mapUserToLocalEntity(u, resp.projectRoles[index]));
        return resp.users.filter(u => !u.disabled);
    });
}

function fetchUsersForOrganization(organizationId): JQueryPromise<IUser[]> {
    return Ajax({
        url: 'ajax_get_organization_users.json',
        method: 'get',
        data: {
            organizationId
        }
    }).then(resp => {
        if (!resp.users) return [];
        resp.users.forEach((u: IUser, index) => mapUserToLocalEntity(u, resp.users[index]));
        return resp.users.filter(u => !u.disabled);
    });
}

function fetchUsersWithEmails(): JQueryPromise<IUser[]> {
    return Ajax({
        url: 'api/1/users/with_email.json',
        method: 'get'
    }).then(resp => {
        resp.users.forEach((u: IUser, index) => mapUserToLocalEntity(u, null));
        return resp.users.filter(u => !u.disabled);
    });
}

function fetchUsersIam(organizationUuid: string, projectUuid: string, limit: number, offset: number, username?: string, email?: string): Promise<any> {
    const data: any = {
        limit,
        page: offset / limit,
        ...(username ? { username: '%' + username + '%' } : null),
        ...(email ? { email: '%' + email + '%' } : null),
    };

    if (organizationUuid) data.organizationUuid = organizationUuid;
    if (projectUuid) data.projectUuid = projectUuid;

    return Ajax2({
        url: kuura.getIamAddress() + '/users',
        useToken: true,
        data
    });
}

function fetchProjectsForCurrentUser(): JQueryPromise<IProject[]> {
    return Ajax({
        url: 'api/1/projects/list.json',
        method: 'get'
    }).then(resp => {
        return resp.projects;
    });
}

function findOrganizationsForCurrentUser(nameStartsWith: string, privileges: string[] = [], page = 0, pageSize = 15): Promise<{
    organizations: IOrganization[],
    totalRows: number
}> {
    return Ajax2({
        url: kuura.getContextPath() + `/api/1/user/${kuura.getUserUuid()}/available-organizations`,
        method: 'get',
        contentType: 'urlencoded',
        data: {
            name: nameStartsWith.trim(),
            privileges: privileges.join(','),
            page,
            size: pageSize
        }
    });
}

function changeProject(projectId: number) {
    return Ajax({
        url: 'ajax_change_project.json',
        method: 'post',
        data: {
            projectId
        }
    });
}

function changeProjectByUuid(projectUuid: string) {
    return Ajax({
        url: 'ajax_change_project.json',
        method: 'post',
        data: {
            projectUuid
        }
    });
}

async function changeProjectWithConfirm(targetProjectId: number, confirmMsg: string): Promise<{status: string}> {
    if (targetProjectId == kuura.getProjectId()) { // no === here, one side can have integer other string
        return Promise.resolve({ status: 'same_project' });
    }
    else {
        try {
            const userProjects = await fetchProjectsForCurrentUser();
            if (userProjects.map(p => p.id).includes(targetProjectId)) {
                if (confirm(confirmMsg)) {
                    try {
                        await changeProject(targetProjectId);
                        window.location.reload();
                        return Promise.resolve({ status: 'switching' });
                    }
                    catch {
                        Notify.error('Failed to switch project');
                        return Promise.reject({ status: 'error' });
                    }
                }
                else {
                    return Promise.resolve({ status: 'canceled' });
                }
            }
            else {
                Notify.error(kuura.getMessage('general.validation.access_not_allowed'));
                return Promise.reject({ status: 'forbidden' });
            }
        }
        catch {
            Notify.error('Failed to switch project');
            return Promise.reject({ status: 'error' });
        }
    }
}

function uniqueRolesForUsers(users: IUser[]): IUserRole[] {
    const set = {};
    users.forEach(u => u.roles.forEach(r => set[r.uuid ? r.uuid : r.id] = r));
    return Object.entries(set).map(([key, val]) => val as IUserRole);
}

function mapUserToLocalEntity(u: IUser, projectRole: any): IUser {
    u.fullName = UsersService.getFullName(u);
    if (!u.fullName) {
        u.fullName = u.username
    }
    u.fullNameLowercase = u.fullName.toLowerCase();
    u.fullNameLowercaseLatin = removeDiacritics(u.fullNameLowercase);
    u.roles.forEach(r => r.friendlyName = r.name.replace('ROLE_', '').replace('_', ' '));
    u.rolesString = u.roles.map(r => r.friendlyName).join(', ');
    u.rolesIndex = _.indexBy(u.roles, 'id');
    u.groups = [];

    if (typeof projectRole === 'string') {
        u.projectRole = projectRole;
        u.projectRoleLowercaseLatin = removeDiacritics(u.projectRole.toLowerCase());
    }
    return u;
}

function isPasswordComplex(val: string): boolean {
    // at least eight characters and contain both numbers, uppercase and lowercase letters, no whitespace
    return val.length >= 8 && !!val.match(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d]{8,}$/);
}

export const UsersService = {
    fetchUsersForOrganization,
    fetchUsersForCurrentProject,
    fetchUsersWithEmails,
    fetchProjectsForCurrentUser,
    findOrganizationsForCurrentUser,
    changeProject,
    changeProjectByUuid,
    changeProjectWithConfirm,
    uniqueRolesForUsers,
    mapUserToLocalEntity,
    isPasswordComplex,
    getFullName: (u: any) => [u.firstname, u.firstName, u.lastname, u.lastName].filter(Boolean).join(' ') ?? u.username,
    getByUuid: (uuid: string) => Ajax2({
        url: kuura.getContextPath() + '/v1/user/' + uuid
    })
};
